import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal bar chart is a data visualization tool that represents categorical data using
          rectangular bars oriented horizontally. The length of each bar corresponds to the value or
          magnitude of the data it represents, making it ideal for comparing categories, especially
          when category labels are long or when emphasizing rankings or differences among groups.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Horizontal bar charts are useful when</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Long Category Labels:</strong> When categories have long names or
            descriptions, horizontal bar charts make it easier to read the labels without crowding,
            making them ideal for datasets like survey results or employee performance.
          </li>
          <li>
            <strong>Comparing Data Across Categories:</strong> Horizontal bar charts are effective
            for comparing data across categories, especially when the categories are ranked, such as
            sales figures by region or job satisfaction across departments.
          </li>
          <li>
            <strong>Visualizing Rankings or Hierarchical Data:</strong> They are excellent for
            showing rankings, such as top-selling products, market share by company, or performance
            in a competitive scenario, where the order of importance is clear.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal bar charts are a powerful visualization tool, but they come with limitations
          that must be considered when selecting them for specific data sets or analyses:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Space for Category Labels:</strong> Long category names can cause
            overcrowding or overlap in horizontal bar charts, making it difficult to read or
            interpret the data accurately.
          </li>
          <li>
            <strong>Less Effective for Time-Based Data:</strong> Horizontal bar charts are not ideal
            for visualizing trends over time, as vertical bar charts or line charts are better
            suited for displaying temporal changes.
          </li>
          <li>
            <strong>Challenges with Large Datasets:</strong> When dealing with many categories,
            horizontal bar charts can become cluttered and hard to read, making it difficult to
            extract meaningful insights.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection plays a crucial role in horizontal bar charts, ensuring clarity and
          effective communication of data. Using bold, distinct colors helps differentiate
          categories and makes comparisons more intuitive, while softer tones can highlight
          overarching trends without distracting the viewer. Gradients can illustrate intensity or
          progression across the bars, enhancing the visual appeal and depth of the chart.
          Thoughtful color choices help direct attention, maintain readability, and avoid
          unnecessary visual complexity.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal bar charts are a powerful visualization tool, but they come with certain
          challenges that should be considered when deciding whether to use them:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Limited Vertical Space:</strong> Horizontal bar charts may struggle when
            displaying a large number of categories, as the available vertical space becomes
            limited, making the chart cluttered or causing bars to be too short to be useful.
          </li>
          <li>
            <strong>Difficulty with Long Labels:</strong> Although horizontal bars offer more room
            for long category labels, they can still become overwhelming if the labels are
            excessively long, leading to text overlap or readability issues.
          </li>
          <li>
            <strong>Less Effective for Time-Based Data:</strong> Horizontal bar charts may not work
            well for visualizing time-series data or trends, as they are better suited for
            categorical comparisons rather than showing changes over time.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
